import { Paginated } from './utils';

export enum ErrorCodes {
  REGISTER_EXISTING_USER = 1000,
  REGISTER_PASSWORD_CONFIRMATION = 1001,
  REGISTER_ACCEPT_TOS = 1002,
  REGISTER_PASSWORD_WEAK = 1003,
  REGISTER_PASSWORD_LENGTH = 1004,

  LOGIN_INVALID_CREDENTIALS = 2001,
  LOGIN_UNKNOWN_USER = 2002,

  INVALID_TOKEN = 3001,
  ALREADY_EXISTS = 3002,

  GENERAL_NOT_FOUND = 4000,
}

export type UploadSource =
  | 'cv'
  | 'userAvatar'
  | 'document'
  | 'user'
  | 'organizations'
  | 'other'
  | 'job';

export interface ErrorMessage {
  code: ErrorCodes;
  message: string;
}

export interface Item<IdType = number> {
  id: IdType;
  name: string;
}

export interface Choice {
  id?: number;
  name: string;
}

export interface PhoneNumber {
  countryCode: string;
  number: string;
}

export interface Badge {
  image: string;
  content: string;
}

export interface JobViewed {
  id: number;
  createdAt: Date;
}

export interface JobSaved {
  id: number;
  createdAt: Date;
}

export interface CoverLetter {
  id: number;
  title: string;
  content: string;
  createdAt: Date;
}

export interface UpdateCoverLetter {
  id: number;
  title: string;
  content: string;
}

export interface UserApplicationEmployerStatus {
  forwardedToAt: Date | null;
  seenAt: Date | null;
  endedAt: Date | null;
  haveFeedback: boolean | null;
}

interface UserJobApply {
  id: JobDetails['id'];
  cvDocumentId?: MpFile['id'];
  coverLetter?: string;
  coverLetterDocumentId?: MpFile['id'];
  otherDocumentsDocumentIds: MpFile['id'][];
  //questionnaire?: QuestionUserAnswersMap;
}

export interface UserJobApplyFull extends UserJobApply {
  attachProfile: boolean;
}

export interface UserJobApplyGuest extends UserJobApply {
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  email: string;
}

//export type QuestionUserAnswersMap = Record<QuestionnaireQuestion['id'], string | number | number[]>;

export type Language = Item<string>;

export type LanguageLevel = Item<string>;

export type Nationality = Item;

export type EducationLevel = Item;

export type WorkExperience = Item;

export type SkillCategory = Item;

export type DriverLicence = Item;

export type JobFormatGroup = Item;

export type Position = Item & {
  imageUrl?: string;
};

export type FacebookLogin = SocialAccountLogin & {
  userId: string;
};

export interface SocialAccountLogin {
  accessToken: string;
}

export interface QuestionType extends Item {
  name: 'textfield' | 'textarea' | 'radio' | 'checkbox' | 'select';
}

/*export interface Questionnaire extends Item {
  questions: QuestionnaireQuestion[];
}

export interface QuestionType extends Item {
  name: 'textfield' | 'textarea' | 'radio' | 'checkbox' | 'select';
}

export interface QuestionnaireQuestion {
  id: number;
  question: string;
  isRequired: boolean;
  questionType: QuestionType;
  answers: QuestionAnswer[];
}

export interface QuestionAnswer {
  id: number;
  answer: string;
}*/

export interface Logo {
  id: number;
  url: string;
}

export type FormatName =
  | 'MP_INVISIBLE'
  | 'MPP_FREE'
  | 'PROMO'
  | 'EXCLUSIVE-HOME'
  | 'FEATURED-HOME'
  | 'EXCLUSIVE'
  | 'FEATURED'
  | 'HIGHLIGHTED'
  | 'STANDARD'
  | 'BASIC'
  | 'VIDEO'
  | string;

export interface JobGroup {
  id: string;
  jobs: JobPreview[];
  organization: OrganizationSimple | 'incognito';
  format: FormatName;
  externalUrl?: string | null;
}

export interface JobPreview {
  id: string;
  title: string;
  slug: string;
  isActive: boolean;
  location: string;
  endsAt: Date;
  userInfo: JobUserInfo;
  description?: string;
  isEndNear?: boolean;
  externalDetailsUrl?: string;
  salary?: Salary | null;
  tags?: string[];
  badges?: Badge[];
}

export interface JobSimple extends JobPreview {
  organization: OrganizationSimple | 'incognito';
}

export interface JobApplicationSimple {
  id: string;
  name: string;
  slug: string;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  image: Avatar | null;
  phone: PhoneNumber | null;
  educationLevel: EducationLevel | null;
  address: string | null;
  dateOfBirth: Date | string | null;
  citizenship: string | null;
  contactEmail: string | null;
  linkedin: string | null;
  webSite: string | null;
  needsPasswordUpdate?: boolean;
  notificationsLanguage: Language;
  title?: string | null;
  keywords?: string | null;
  theme: 'light' | 'dark';
  uuid: string;
}

export interface UserProfile {
  id: number;
  createdAt: Date;
  updatedAt: Date | null;
  myCvName: string;
  publishable: boolean;
  visible: boolean;
  workExperience: UserWorkExperience[];
  volunteerExperience: UserWorkExperience[];
  educations: UserEducation[];
  trainings: UserTraining[];
  digitalSkills: UserSkill[];
  otherSkills: UserSkill[];
  languages: UserLanguageLevel[];
  driverLicences: DriverLicence[];
  projects: Achievement[];
  awards: Achievement[];
  conferences: Achievement[];
  bibliography: Achievement[];
  idealJob: IdealJob;
}

export interface Notification extends Item<string> {
  isActive: boolean;
}

interface BaseNotification {
  id: string;
  dateCreated: number;
  seenBySubscriber: boolean;
  isDeleted?: boolean;
}

interface NotificationJobPublished extends BaseNotification {
  event: 'job_published' | 'job_published_by_company';
  payload: {
    id: number;
    uuid: string;
    title: {
      name: string;
    };
    slug: string;
    company: {
      id: number;
      name: string;
    };
  };
}

// interface NotificationApplicationMessage extends BaseNotification {
//   type: 'application_message';
//   payload: Pick<Application, 'id'>;
// }

// export type MyNotification = NotificationJobPublished | NotificationApplicationMessage;

export interface MyNotification extends NotificationJobPublished {}

export interface Achievement extends Item {
  startedOn: Date | string | null;
  endedOn: Date | string | null;
  description: string | null;
}

export interface IdealJob {
  locations: Location[];
  positions: Item[];
  employmentTypes: EmploymentType[];
  salary: Salary | null;
}

export interface Salary {
  from: number | null;
  to: number | null;
  currency: string;
}

export type UserSimple = Pick<User, 'id' | 'email' | 'uuid'>;

export interface JwtResponse {
  expiresIn: string;
  accessToken: string;
}

export interface JwtPayload {
  id: number;
  email: string;
  uuid: string;
  originalToken?: string;
}

export interface JwtPayloadAnonUser {
  sessionId: string;
}

export interface MpFile {
  id: string;
  name: string;
  url: string;
  previewUrl?: string | null;
  size: number;
  type: string;
  createdAt: Date;
  source?: string;
}

export interface FileForUpload extends MpFile {
  clientId: number;
  progress: number;
  success: boolean;
}

export interface FileUploaded extends MpFile {
  clientId?: string;
}

export interface ProfileCv {
  id: number;
  name: string;
  updatedAt: Date;
  active: boolean;
}

/**
 * Job application
 */
export interface UserApplication {
  id: string;
  createdAt: Date;
  job: JobSimple;
  employerStatus?: UserApplicationEmployerStatus;
}

export type ApplicationIntentStatus = 'initiated' | 'completed';
export type ApplicationIntentApplicationType = JobApplicationType;

export interface ApplicationIntent {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  jobId: string;
  applicationType: ApplicationIntentApplicationType;
  status?: ApplicationIntentStatus;
  source?: JobApplicationSource;
}

export interface Application {
  id: number;
  createdAt: Date;
  job: JobSimple;
  data?: JSON;
  employerStatus?: UserApplicationEmployerStatus;
}

export type JobApplicationType =
  | 'full'
  | 'quick'
  | 'phone'
  | 'external'
  | 'info'
  | 'letter'
  | 'personal'
  | 'direct_link';

export interface ApplicationSimple {
  id: number;
  createdAt: Date;
}

export interface JobApplication {
  id: number;
  createdAt: Date;
  externalReference: string;
}

export interface JobApplicationData {
  firstName: string;
  lastName: string;
  phone: PhoneNumber | null;
  email: string;
  nationality: {value: number, label: string} | null,
  coverLetter: string | null;
  saveCoverLetter?: boolean;
  profileCvs: ProfileCv['id'][];
  cvs: MpFile['id'][];
  documents: MpFile['id'][];
  questions: {
    id: number;
    answers: Choice[];
  }[];
}

export type QuestionTypes = 'textfield' | 'textarea' | 'radio' | 'checkbox' | 'select';

export type MatejScenarios =
  | 'application-success-page'
  | 'search-page'
  | 'home-page'
  | 'job-details'
  | 'article-page';

export type JobDetailsSource =
  | 'homepage_recommended'
  | 'homepage_exclusive'
  | 'homepage_featured'
  | 'search_results'
  | 'search_results_recommendations'
  | 'jobs_page'
  | 'applied_page_recommendations'
  | 'jobs_saved_list'
  | 'jobs_saved_list_item_button'
  | 'jobs_applied_list'
  | 'employer_page_jobs_list'
  | 'cancel_external_application'
  | 'apply_expired'
  | 'apply_page_back_button'
  | 'apply_page_error_not_found'
  | 'apply_page_error_already_applied'
  | 'apply_unsupported_application_type'
  | 'old'
  | 'no_slug'
  | 'notifications'
  | string;

export type JobApplicationSource =
  | 'header_continue'
  | 'details_top'
  | 'details_bottom'
  | 'details_applied_tooltip'
  | 'details_design'
  | 'my_profile_bottom_notification'
  | string;

export interface Question<T extends QuestionTypes> {
  id: number;
  type: T;
  question: string;
  choices: Choice[];
  isRequired: boolean;
}

export interface Questionnaire<T = QuestionTypes> {
  questions: (T extends QuestionTypes ? Question<T> : never)[];
}

export interface JobApplicationFooBar {
  id: string;
  cvRequired: boolean;
  profileCvs: ProfileCv[];
  cvs: MpFile[];
  documents: MpFile[];
  data: JobApplicationData;
  questionnaire?: Questionnaire;
  previousCoverLetters?: Array<{id: number, content: string, title: string, createdAt: Date}>;
  needsVerification: boolean;
}

export interface DocumentInfo {
  id: number;
  name: string;
  type: string;
  url: string;
}

export interface Avatar {
  id: number;
  url: string;
  name: string | null;
}

export interface UserWorkExperience {
  id: string;
  position: string;
  companyName: string;
  location: string;
  startedOn: Date | string | null;
  endedOn: Date | string | null;
  description: string;
}

export interface UserTraining {
  id: string;
  institution: Institution;
  orientation: string;
  startedOn: Date | string | null;
  endedOn: Date | string | null;
}

export interface UserEducation extends UserTraining {
  educationLevel: EducationLevel;
}

export interface JobUserInfo {
  isNew: boolean;
  isSeen: boolean;
  isSaved: boolean;
  appliedAt: Date | null;
}

export interface JobSearchUserInfo {
  isSaved: boolean;
}

export interface JobFormat {
  id: number;
  name: FormatName;
  homepage: boolean;
  weightOrder: number;
}

export type JobStatus = Item;

export type AdditionalService = Item;

export type EmploymentType = Item;

export type JobTag = Item;
export type ApplicationType = Item;

export interface App extends Item {
  domain: string;
  jobGroups?: JobGroup[];
}

export interface Location {
  id: number;
  placeId?: string;
  name?: string;
  data?: JSON;
}

export type LocationFull = Required<Pick<Location, 'placeId' | 'name'>> & {
  data: Record<string, unknown>;
};

export interface LocationSimple {
  id: number;
  name: string;
  formattedAddress: string | null;
  lat: number;
  lng: number;
}

export interface LocationData {
  geometry: any;
  address_components: any;
}

export interface GeoCoordinates {
  lat?: number;
  lng?: number;
}

export interface PositionSuggestionsParams {
  positions?: number[];
}

export interface LocationSuggestionsParams extends GeoCoordinates {
  locations?: string[];
}

export interface JobEducationLevelOrientation {
  educationLevel: EducationLevel;
}

export interface UserEducationLevelOrientation extends JobEducationLevelOrientation {
  id: number;
  institution?: string;
  startAt?: Date;
  endsAt?: Date;
}

export interface JobLanguageLanguageLevel {
  language: Language;
  languageLevel: LanguageLevel;
}

export interface JobLanguageLevel {
  language: Language;
  languageLevel: LanguageLevel;
}

export interface UserLanguageLevel extends JobLanguageLevel {
  id: number;
}

export interface LanguageDetails {
  id: string;
  level: string;
}

export interface ItemsInfo {
  items: string[];
  info: string | null;
}

export interface ItemsLocation {
  address: string;
}

export interface JobDetails {
  id: string;
  title: string;
  slug: string;
  location: {
    items: ItemsLocation[];
    summary: string;
  };
  description: string;
  requirements: string;
  benefits: string;
  isDesigned: boolean;
  html: string;
  publishedAt: Date | null;
  startsAt: Date | null;
  endsAt: Date | null;
  salary: Salary | null;
  numberOfWorkers: number | null;
  requiredWorkExperience: string | null;
  organization: OrganizationPreview | 'incognito';
  applicationTypes: ApplicationInfo[];
  languages: LanguageDetails[];
  driversLicense: ItemsInfo;
  educationLevel: ItemsInfo;
  workTypes: string[];
  employmentTypes: string[];
  digitalSkills: string[];
  otherSkills: string[];
  tags: {
    key: string;
    name: string;
  }[];
  additionalPositions: Item[];
  rootPosition?: Item;
  position?: Item;
  userInfo: JobUserInfo;
  isActive: boolean;
  noindex: boolean;
  employer?: JobEmployer;
  externalDetailsUrl?: string;
  hideApplicationLink: boolean;
}

export interface JobEmployer {
  name?: string;
  info?: string;
  address?: string;
  phone?: string;
  web?: string;
}

export interface ApplicationInfo {
  type: JobApplicationType;
  data: ApplicationInfoData | null;
}

export interface ApplicationInfoData {
  mainPhone?: PhoneNumber;
  additionalPhone?: PhoneNumber;
  address?: string;
  zip?: string;
  city?: string;
  applicationLink?: string;
}

export interface Organization {
  id: number;
  name: string;
  info: string;
  description: string;
  email: string;
  taxNumber: string;
  phone: string;
  web: string;
  mainOrganizationLogo?: Logo;
  country: string;
  address: string;
}

export interface OrganizationSimple {
  id: number;
  name: string;
  logoUrl: string | null;
  badges?: Badge[];
}

export interface OrganizationPreview extends OrganizationSimple {
  info?: string; // TODO - remove ?
  address?: string; // TODO - remove ?
  web: string;
  hasProfile: boolean;
  phone?: string;
  tag?: string | null;
}

export interface OrganizationProfile extends OrganizationPreview {
  content: string;
}

/**
 * Dynamic job filters
 */
interface DynamicJobFilterBase<T extends string> {
  id: string;
  name: string;
  type: T;
}

export interface DynamicJobFilterSelect
  extends DynamicJobFilterBase<'single-select' | 'multi-select'> {
  options: Item[];
}

export type DynamicJobFilterToggle = DynamicJobFilterBase<'toggle'>;

export type DynamicJobFilter = DynamicJobFilterSelect | DynamicJobFilterToggle;

/**
 * Pagination
 */
export interface WithPaginationParams {
  page?: number;
}

/**
 * Job search static params
 */
export interface JobSearchStaticParamsItem {
  positions: Item<number>[];
  locations: Item<string>[];
}

export type JobSearchStaticParamsOther = {
  query: string[];
  newJobs: boolean;
};

type JobSearchStaticParamsPlain = {
  [K in keyof JobSearchStaticParamsItem]: JobSearchStaticParamsItem[K] extends Item<infer U>[]
    ? U[]
    : never;
};

export type JobSearchParams = Partial<
  JobSearchStaticParamsPlain & JobSearchStaticParamsOther & WithPaginationParams
>;

/**
 * Job Filter. Combines both static and dynamic filters
 */
export type JobFilterStatic = JobSearchStaticParamsItem &
  JobSearchStaticParamsOther &
  WithPaginationParams;

type JobFilterDynamic = Record<string, Item | Item[] | Item<string> | Item<string>[] | boolean>;

export type JobFilter = {
  static: JobFilterStatic;
  dynamic: JobFilterDynamic;
};

export type FeaturedJobSearchGroupKey =
  | 'city'
  | 'international'
  | 'popular'
  | 'position'
  | 'region';

export interface FeaturedJobSearchCard {
  key: FeaturedJobSearchGroupKey;
  items: JobSearch[];
}

export interface JobSearch {
  id: number;
  title?: string;
  subtitle?: string;
  description?: string;
  newJobsCount?: number;
  params?: JobFilter;
}

/**
 * Number of ads that correspond  to a specific filter constraint
 * How it works: Filters (filter options) are saved in the db
 * If a filter option name(key) is the same as a FilterCount
 * on the frontend we need to display number of ads alongside
 * the filter option
 */
export interface FilterCount {
  new?: number;
}

export interface JobSearchResult extends Paginated<JobGroup> {
  filterCount?: FilterCount;
  search: Pick<JobSearch, 'id' | 'title'>;
  userInfo: JobSearchUserInfo;
}

export type Suggestion = Item<string | number>;

export interface Suggestions {
  key: string;
  items: Suggestion[];
  additionalItems: Suggestion[];
}

export interface GroupedSuggestions {
  primary: Suggestions[];
  secondary: Suggestions[];
}

export interface AppLink {
  url: string;
  title: string;
  iconUrl: string | null;
}

export interface Page {
  title: string;
  description: string;
  subtitle: string;
  content: JSON;
  html: string;
}

export interface Article {
  id: number;
  title: string;
  header: string;
  imageUrl: string;
  link: string;
}

export interface Login {
  email: string;
  password: string;
}

export interface Register extends Login {
  firstName: string;
  lastName: string;
  educationLevel?: User['educationLevel'];
  address?: string;
}

export interface Verify {
  email: string;
  verificationCode: string;
}

export interface ChangeEmail {
  email: string;
}

export interface ChangePassword {
  oldPassword: string;
  newPassword: string;
}

export interface Institution {
  id?: number;
  name: string;
}

export const SkillLevels = ['basic', 'advanced', 'professional'] as const;

export type SkillLevel = (typeof SkillLevels)[number];

export interface UserSkill {
  id: string;
  skill: Skill;
  level: SkillLevel;
  description: string;
}

export interface Skill {
  id?: number;
  name: string;
}

export interface PendingApplication {
  id: string;
  job: JobApplicationSimple;
}

export interface UTM {
  campaign?: string;
  source?: string;
  medium?: string;
  term?: string;
  content?: string;
}

export type CalculatorMode = 'salary' | 'author' | 'service';

export interface ApplicationActions {
  shouldRegister: boolean;
  shouldSaveSearch: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

export type AchievementType = 'projects' | 'awards' | 'conferences' | 'bibliography';

export type SkillType = 'digital' | 'other';

export interface MyTempMessage {
  id: string;
  organization: string;
  subject: string;
  content: string;
  createdAt: Date;
  isRead: boolean;
}
